import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import { Stack } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { grey } from '@mui/material/colors';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import * as uuid from 'uuid';

import MyCard from '../../components/MyCard/MyCard';
import MyPageHeader from '../../components/MyPageHeader/MyPageHeader';
import IconSidebar from '../../Layout/IconSidebar/IconSidebar';
import Navbar from '../../Layout/Navbar/Navbar';
import { deleteEvaAgentAPI } from '../../services/deleteEvaAgent.service';
import { editEvaAgentAPI } from '../../services/editEvaAgent.service';
import { getEvaAgentsAPI } from '../../services/getEvaAgents.service';
import { postEvaAgentAPI } from '../../services/postEvaAgent.service';

import styles from './EvaAgents.module.css';

const emptyAgent = { name: '', description: '', url: '' };

export default function EvaAgents() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const userInfo = JSON.parse(localStorage.getItem('userInfo'));

  const [rows, setRows] = useState([]);
  const [pageSize, setPageSize] = useState(5);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [agent, setAgent] = useState(emptyAgent);

  const loadAgents = () => {
    getEvaAgentsAPI()
      .then((res) => {
        const data = res.data.map((val) => ({ ...val, _id: val.id ?? uuid.v4() }));
        setRows(data);
      })
      .catch((e) => console.log(e));
  };

  useEffect(() => {
    if (userInfo?.role.findIndex((p) => p === 'Administrador') < 0) {
      alert('No tiene permiso para acceder a esta funcionalidad');
      navigate('/dashboard');
      return;
    }
    loadAgents();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenModal = () => {
    setAgent(emptyAgent);
    setEdit(false);
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setEdit(false);
    setAgent(emptyAgent);
  };

  const handleChange = (e) => {
    setAgent({ ...agent, [e.target.name]: e.target.value });
  };

  const handleSave = () => {
    if (!agent.name.trim() || !agent.url.trim()) {
      enqueueSnackbar('Por favor complete todos los campos', { variant: 'warning' });
      return;
    }
    if (edit) {
      editEvaAgentAPI(agent.id, { name: agent.name, description: agent.description, url: agent.url, isAnalysis: true })
        .then(() => {
          setRows((prev) =>
            prev.map((r) => (r._id === agent._id ? { ...agent } : r))
          );
          enqueueSnackbar('Agente actualizado con éxito', { variant: 'success' });
        })
        .catch(() =>
          enqueueSnackbar('Error al actualizar el agente', { variant: 'error' })
        );
    } else {
      postEvaAgentAPI({ name: agent.name, description: agent.description, url: agent.url, isAnalysis: true })
        .then((res) => {
          const newAgent = { ...res.data, _id: res.data.id ?? uuid.v4() };
          setRows((prev) => [...prev, newAgent]);
          enqueueSnackbar('Agente creado con éxito', { variant: 'success' });
        })
        .catch(() =>
          enqueueSnackbar('Error al crear el agente', { variant: 'error' })
        );
    }
    handleCloseModal();
  };

  const handleEdit = (row) => {
    setAgent({ ...row });
    setEdit(true);
    setOpen(true);
  };

  const handleDelete = (row) => {
    deleteEvaAgentAPI(row.id)
      .then(() => {
        setRows((prev) => prev.filter((r) => r._id !== row._id));
        enqueueSnackbar('Agente eliminado con éxito', { variant: 'success' });
      })
      .catch(() =>
        enqueueSnackbar('Error al eliminar el agente', { variant: 'error' })
      );
  };

  const columns = [
    {
      field: 'name',
      flex: 1,
      headerName: 'Nombre',
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'description',
      flex: 1,
      headerName: 'Descripción',
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'url',
      flex: 2,
      headerName: 'URL',
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      width: 120,
      getActions: (params) => [
        <IconButton onClick={() => handleEdit(params.row)}>
          <EditIcon />
        </IconButton>,
        <IconButton onClick={() => handleDelete(params.row)}>
          <DeleteIcon />
        </IconButton>,
      ],
    },
  ];

  return (
    <Box sx={{ display: 'flex' }}>
      <Navbar />
      <IconSidebar />

      <Modal open={open || edit} onClose={handleCloseModal}>
        <Box className={styles.modal}>
          <div className={styles.modaltop}>
            <Typography variant="h6">
              {edit ? 'Editar' : 'Nuevo'} Agente EVA
            </Typography>
            <IconButton onClick={handleCloseModal}>
              <ClearIcon sx={{ fontSize: '32px' }} />
            </IconButton>
          </div>
          <div className={styles.modalform}>
            <TextField
              label="Nombre"
              name="name"
              value={agent.name}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
            <TextField
              label="Descripción"
              name="description"
              value={agent.description}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
            <TextField
              label="URL"
              name="url"
              value={agent.url}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </div>
          <div className={styles.modalactions}>
            <Button variant="outlined" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button variant="contained" color="primary" onClick={handleSave}>
              {edit ? 'Guardar cambios' : 'Crear'}
            </Button>
          </div>
        </Box>
      </Modal>

      <div style={{ backgroundColor: 'rgb(224, 219, 219)' }}>
        <div className={styles.EvaAgentsPage}>
          <div className={styles.EvaAgentsPage__content}>
            <MyPageHeader title="Agentes EVA" needBack={false} />
            <MyCard>
              <Stack spacing={2} direction="row-reverse" sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleOpenModal}
                >
                  Nuevo agente
                </Button>
              </Stack>
              <DataGrid
                rows={rows}
                columns={columns}
                getRowId={(row) => row._id}
                rowsPerPageOptions={[5, 10, 20]}
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                autoHeight
                getRowSpacing={(params) => ({
                  top: params.isFirstVisible ? 0 : 5,
                  bottom: params.isLastVisible ? 0 : 5,
                })}
                sx={{
                  [`& .${gridClasses.row}`]: {
                    bgcolor: (theme) =>
                      theme.palette.mode === 'light' ? grey[200] : grey[900],
                  },
                }}
              />
            </MyCard>
          </div>
        </div>
      </div>
    </Box>
  );
}
